import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { Camera, Trophy, Flame, TrendingUp, Clock } from 'lucide-react'

interface ActivityItem {
  type: 'scan' | 'achievement' | 'level_up' | 'streak'
  title: string
  description: string
  xp?: number
  timestamp: string
}

interface ChildActivityFeedProps {
  studentId: number
  limit?: number
}

// Format relative time
function timeAgo(date: string): string {
  const now = new Date()
  const then = new Date(date)
  const diffMs = now.getTime() - then.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  return then.toLocaleDateString()
}

const activityIcons = {
  scan: <Camera className="w-4 h-4 text-blue-400" />,
  achievement: <Trophy className="w-4 h-4 text-purple-400" />,
  level_up: <TrendingUp className="w-4 h-4 text-green-400" />,
  streak: <Flame className="w-4 h-4 text-orange-400" />,
}

export default function ChildActivityFeed({ studentId, limit = 5 }: ChildActivityFeedProps) {
  const { data, isLoading } = useQuery<{ activities: ActivityItem[] }>({
    queryKey: ['child-activity', studentId],
    queryFn: async () => {
      const response = await api.get(`/api/parent/student/${studentId}/activity?limit=${limit}`)
      return response.data
    },
    staleTime: 30000, // 30 seconds
  })

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-10 bg-gray-700/50 rounded animate-pulse" />
        ))}
      </div>
    )
  }

  const activities = data?.activities || []

  if (activities.length === 0) {
    return (
      <div className="text-center py-4 text-gray-500 text-sm">
        No recent activity
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {activities.map((activity, index) => (
        <div
          key={index}
          className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-700/30 transition-colors"
        >
          <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center flex-shrink-0">
            {activityIcons[activity.type]}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{activity.title}</p>
            <p className="text-xs text-gray-400 truncate">{activity.description}</p>
          </div>
          <div className="text-right flex-shrink-0">
            {activity.xp && (
              <span className="text-xs font-medium text-quest-primary">+{activity.xp} XP</span>
            )}
            <p className="text-xs text-gray-500 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {timeAgo(activity.timestamp)}
            </p>
          </div>
        </div>
      ))}
    </div>
  )
}
